<template>
  <div class="pa-4 dashboard-bg min-h-screen">
    <!-- Action Header -->
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        small
        @click="$router.push({ name: 'app-admin-emergency-order-list' })"
        class="mr-2"
      >
        <v-icon small>mdi-arrow-left</v-icon>
      </v-btn>
      <span
        class="text-subtitle-2 font-weight-bold grey--text text--darken-1 mr-2"
        >Emergency Orders</span
      >
      <v-icon x-small color="grey lighten-1">mdi-chevron-right</v-icon>
      <span class="text-subtitle-2 font-weight-black primary--text ml-2">{{
        orderDetails.e_token || id
      }}</span>

      <v-spacer></v-spacer>

      <!-- <v-btn
        v-if="
          orderDetails.status !== 'Completed' &&
          orderDetails.status !== 'Cancelled' &&
          !daDetails.das?.length &&
          orderDetails.stage !== 'Drop'
        "
        color="primary"
        depressed
        small
        @click="openConfirmDA"
        class="font-weight-bold px-4"
      >
        <v-icon left x-small>mdi-check-circle</v-icon>
        Confirm DA
      </v-btn> -->
    </div>

    <v-row v-if="loading" justify="center" align="center" style="height: 60vh">
      <v-progress-circular
        indeterminate
        color="primary"
        size="40"
      ></v-progress-circular>
    </v-row>

    <v-row v-else>
      <!-- Left Column -->
      <v-col cols="12" lg="8">
        <!-- Order Details -->
        <v-card flat class="rounded-lg mb-3 border">
          <v-card-title
            class="text-subtitle-2 font-weight-bold py-2 px-4 grey lighten-5"
          >
            Order Details
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-3">
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Status</div>
                <div
                  class="data-value font-weight-black"
                  :class="statusColor(orderDetails.status) + '--text'"
                >
                  {{ orderDetails.status || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Current Stage</div>
                <div class="data-value font-weight-black primary--text">
                  {{ orderDetails.stage || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Order Date</div>
                <div class="data-value">
                  {{ orderDetails.order_date || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Hire Type</div>
                <v-chip
                  label
                  x-small
                  :color="
                    orderDetails.is_sub_hire
                      ? 'amber lighten-4'
                      : 'blue lighten-4'
                  "
                  :class="
                    orderDetails.is_sub_hire
                      ? 'amber--text text--darken-4'
                      : 'blue--text text--darken-4'
                  "
                  class="font-weight-black mt-1"
                >
                  {{ orderDetails.is_sub_hire ? "SUB-HIRE" : "OWN FLEET" }}
                </v-chip>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Job Type</div>
                <div class="data-value">
                  {{ jobType }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">VBS Reference Number</div>
                <div class="d-flex align-center" style="gap: 6px">
                  <div class="data-value">
                    {{ orderDetails.vbs_reference_number || "-" }}
                  </div>
                  <v-tooltip v-if="orderDetails.failure_reasons" right max-width="320">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        x-small
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon small>mdi-alert-circle</v-icon>
                      </v-btn>
                    </template>
                    <div>
                      <div class="font-weight-bold mb-1">System Blockers</div>
                      <div
                        v-for="(reason, i) in orderDetails.failure_reasons.split(',')"
                        :key="i"
                      >
                        &bull; {{ reason.trim() }}
                      </div>
                    </div>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Cargo Flags</div>
                <div class="d-flex align-center mt-1" style="gap: 6px">
                  <v-chip
                    v-if="orderDetails.is_oog"
                    label
                    x-small
                    color="deep-orange lighten-4"
                    class="deep-orange--text text--darken-4 font-weight-black"
                    >OOG</v-chip
                  >
                  <v-chip
                    v-if="orderDetails.is_hazardous"
                    label
                    x-small
                    color="red lighten-4"
                    class="red--text text--darken-4 font-weight-black"
                    >HAZARDOUS</v-chip
                  >
                  <v-chip
                    v-if="orderDetails.is_reefer"
                    label
                    x-small
                    color="light-blue lighten-4"
                    class="light-blue--text text--darken-4 font-weight-black"
                    >REEFER</v-chip
                  >
                  <span
                    v-if="
                      !orderDetails.is_oog &&
                      !orderDetails.is_hazardous &&
                      !orderDetails.is_reefer
                    "
                    class="data-value"
                    >-</span
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Customer</div>
                <div class="data-value">
                  {{ orderDetails.customer_name || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Vendor</div>
                <div class="data-value">
                  {{ orderDetails.vendor_name || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Vehicle Number</div>
                <div class="d-flex align-center">
                  <div
                    class="text-subtitle-2 font-weight-black primary--text tracking-tight mr-1"
                  >
                    {{ executionDetails.vehicle_number || "-" }}
                  </div>
                  <v-btn
                    v-if="executionDetails.vehicle_number"
                    icon
                    x-small
                    @click="copyVehicle(executionDetails.vehicle_number)"
                  >
                    <v-icon x-small color="grey">mdi-content-copy</v-icon>
                  </v-btn>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label">Driver Name</div>
                <div class="data-value">
                  {{ executionDetails.driver_name || "-" }}
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label mb-2">System Trip</div>
                <div class="d-flex flex-wrap align-center">
                  <template v-if="daDetails.trips && daDetails.trips.length">
                    <v-chip
                      v-for="trip in daDetails.trips"
                      :key="trip.id"
                      x-small
                      color="primary lighten-5"
                      class="mr-2 mb-1 primary--text font-weight-bold"
                      style="cursor: pointer"
                      @click="openTrip(trip.id)"
                    >
                      {{ trip.trip_id
                      }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
                    </v-chip>
                  </template>
                  <template
                    v-if="orderDetails.is_sub_hire && subHireList.length"
                  >
                    <v-chip
                      v-for="shj in subHireList"
                      :key="shj.id"
                      x-small
                      color="amber lighten-4"
                      class="mr-2 mb-1 amber--text text--darken-4 font-weight-bold"
                      style="cursor: pointer"
                      @click="
                        $router.push({
                          name: subHireRouteName(shj.status),
                          query: { search: shj.sub_hire_id },
                        })
                      "
                    >
                      {{ shj.sub_hire_id
                      }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
                    </v-chip>
                  </template>
                  <span
                    v-if="
                      !(daDetails.trips && daDetails.trips.length) &&
                      !(orderDetails.is_sub_hire && subHireList.length)
                    "
                    class="text-caption grey--text"
                    >-</span
                  >
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <div class="data-label mb-2">Delivery Advices</div>
                <div class="d-flex flex-wrap align-center">
                  <template v-if="daDetails.das && daDetails.das.length">
                    <v-chip
                      v-for="da in daDetails.das"
                      :key="da.id"
                      x-small
                      color="primary lighten-5"
                      class="mr-2 mb-1 primary--text font-weight-bold"
                      style="cursor: pointer"
                      @click="openDA(da.id)"
                    >
                      {{ da.da_no
                      }}<v-icon x-small class="ml-1">mdi-open-in-new</v-icon>
                    </v-chip>
                  </template>
                  <span v-else class="text-caption grey--text"
                    >Not Generated</span
                  >
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Container Table -->
        <v-card flat class="rounded-lg border overflow-hidden mb-3">
          <v-card-title
            class="text-subtitle-2 font-weight-bold py-2 px-4 grey lighten-5"
          >
            Attached Containers
            <v-spacer></v-spacer>
            <span class="text-caption grey--text"
              >{{ containers.length }} Unit(s)</span
            >
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-2">
            <div v-if="!containers.length" class="text-caption grey--text text-center py-4">
              No containers found.
            </div>
            <v-row dense>
              <v-col
                v-for="(c, i) in containers.map((c, i) => ({
                  ...c,
                  index: i + 1,
                  ...daLaneByDaNo[c.da_no],
                }))"
                :key="i"
                cols="12"
              >
                <v-card outlined class="rounded-lg" style="overflow: hidden">
                  <!-- Container header row -->
                  <div
                    class="d-flex align-center px-3 py-2 grey lighten-5"
                    style="cursor: pointer"
                    @click="
                      $set(
                        expandedContainers,
                        c.number,
                        !expandedContainers[c.number],
                      )
                    "
                  >
                    <span class="text-caption grey--text mr-2"
                      >#{{ c.index }}</span
                    >
                    <span
                      class="font-weight-black primary--text mr-3"
                      style="font-size: 13px"
                      >{{ c.number }}</span
                    >
                    <v-chip
                      x-small
                      outlined
                      color="primary"
                      class="font-weight-bold mr-3"
                      >{{ c.type }}</v-chip
                    >
                    <v-chip
                      v-if="c.da_no"
                      x-small
                      color="amber lighten-4"
                      class="font-weight-bold mr-2"
                      style="cursor:pointer"
                      @click.stop="openDA(c.da_id)"
                    >{{ c.da_no }}<v-icon x-small right>mdi-open-in-new</v-icon></v-chip>
                    <v-chip
                      v-if="c.trip_no"
                      x-small
                      color="blue lighten-4"
                      class="font-weight-bold mr-2"
                      style="cursor:pointer"
                      @click.stop="openTrip(c.trip_id)"
                    >{{ c.trip_no }}<v-icon x-small right>mdi-open-in-new</v-icon></v-chip>
                    <v-chip
                      v-if="c.sub_hire_no"
                      x-small
                      color="purple lighten-4"
                      class="font-weight-bold mr-3"
                      style="cursor:pointer"
                      @click.stop="$router.push({ name: subHireRouteName(c.sub_hire_status), query: { search: c.sub_hire_no } })"
                    >{{ c.sub_hire_no }}<v-icon x-small right>mdi-open-in-new</v-icon></v-chip>
                    <span v-if="!c.da_no" class="text-caption grey--text font-weight-bold mr-3">-</span>
                    <span class="text-caption mr-2">{{ c.origin || "-" }}</span>
                    <v-icon x-small class="mx-1 grey--text"
                      >mdi-arrow-right</v-icon
                    >
                    <span
                      class="text-caption font-weight-medium"
                      style="color: #e65100"
                      >{{ c.destination || "-" }}</span
                    >
                    <v-spacer />
                    <!-- Actions -->
                    <v-tooltip
                      top
                      v-if="
                        orderDetails.stage === 'Drop' &&
                        orderDetails.status !== 'Completed' &&
                        orderDetails.status !== 'Cancelled' &&
                        !c.da_no
                      "
                    >
                      <!-- <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-on="on"
                          v-bind="attrs"
                          @click.stop="openCreateDAForContainer(c)"
                        >
                          <v-icon x-small color="primary"
                            >mdi-file-plus-outline</v-icon
                          >
                        </v-btn>
                      </template> -->
                      <span>Create DA</span>
                    </v-tooltip>
                    <v-tooltip
                      top
                      v-if="
                        orderDetails.status !== 'Completed' &&
                        orderDetails.status !== 'Cancelled' &&
                        c.da_no &&
                        !!c.da_no
                      "
                    >
                      <!-- <template #activator="{ on, attrs }">
                        <v-btn
                          x-small
                          icon
                          v-on="on"
                          v-bind="attrs"
                          @click.stop="openChangeLane(c)"
                        >
                          <v-icon x-small color="warning darken-1"
                            >mdi-map-marker-path</v-icon
                          >
                        </v-btn>
                      </template> -->
                      <span>Change Lane</span>
                    </v-tooltip>
                    <v-icon x-small class="ml-1 grey--text">
                      {{
                        expandedContainers[c.number]
                          ? "mdi-chevron-up"
                          : "mdi-chevron-down"
                      }}
                    </v-icon>
                  </div>
                  <!-- Milestone expand -->
                  <v-expand-transition>
                    <div v-if="expandedContainers[c.number]">
                      <v-divider />
                      <div
                        class="d-flex pa-3"
                        style="gap: 12px; flex-wrap: wrap"
                      >
                        <div
                          v-for="ms in [
                            {
                              key: 'pickup_arrival',
                              label: 'Pickup Arrival',
                              icon: 'mdi-truck-check',
                              color: 'blue',
                            },
                            {
                              key: 'pickup_departure',
                              label: 'Pickup Departure',
                              icon: 'mdi-truck-delivery',
                              color: 'indigo',
                            },
                            {
                              key: 'drop_arrival',
                              label: 'Drop Arrival',
                              icon: 'mdi-map-marker-check',
                              color: 'green',
                            },
                          ]"
                          :key="ms.key"
                          class="d-flex align-center pa-2 rounded border"
                          style="min-width: 160px; flex: 1"
                        >
                          <div
                            class="tracker-dot mr-2 flex-shrink-0"
                            :class="
                              getContainerMilestoneTs(c.number, ms.key)
                                ? ms.color
                                : 'grey lighten-2'
                            "
                          >
                            <v-icon x-small color="white">
                              {{
                                getContainerMilestoneTs(c.number, ms.key)
                                  ? ms.icon
                                  : "mdi-circle-outline"
                              }}
                            </v-icon>
                          </div>
                          <div>
                            <div
                              class="text-caption grey--text"
                              style="font-size: 9px; line-height: 1.2"
                            >
                              {{ ms.label.toUpperCase() }}
                            </div>
                            <div
                              class="text-caption font-weight-medium"
                              style="font-size: 10px"
                              :class="
                                getContainerMilestoneTs(c.number, ms.key)
                                  ? 'grey--text text--darken-3'
                                  : 'grey--text'
                              "
                            >
                              {{
                                getContainerMilestoneTs(c.number, ms.key) ||
                                "Pending"
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

      </v-col>

      <!-- Right Column: Activity Log -->
      <v-col cols="12" lg="4">
        <!-- Activity Log -->
        <v-card flat class="rounded-lg border overflow-hidden">
          <v-card-title
            class="text-subtitle-2 font-weight-bold py-2 px-4 grey lighten-5"
          >
            Activity Log
            <v-spacer></v-spacer>
            <span class="text-caption grey--text"
              >{{ logs.length }} event(s)</span
            >
          </v-card-title>
          <v-divider></v-divider>
          <div class="log-scroll-area px-3 py-2" id="log-box">
            <div v-if="logsLoading" class="text-center py-4">
              <v-progress-circular
                indeterminate
                color="primary"
                size="20"
              ></v-progress-circular>
            </div>
            <template v-else-if="logs.length">
              <div
                v-for="(log, lIdx) in logs"
                :key="lIdx"
                class="log-v-card mb-2"
              >
                <div class="log-v-card-status">
                  <span
                    class="font-weight-black"
                    :class="statusColor(log.status) + '--text'"
                    style="
                      font-size: 9px;
                      text-transform: uppercase;
                      letter-spacing: 0.5px;
                    "
                    >{{ log.status }}</span
                  >
                  <span class="grey--text ml-2" style="font-size: 9px">{{
                    formatDate(log.created)
                  }}</span>
                </div>
                <div class="log-v-card-body">
                  <div
                    class="grey--text text--darken-3"
                    style="font-size: 11px; line-height: 1.5"
                  >
                    {{ log.message }}
                  </div>
                  <div
                    v-if="log.added_by"
                    class="grey--text mt-1"
                    style="font-size: 9px"
                  >
                    By: {{ log.added_by }}
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="text-center py-4 grey--text text-caption">
              No activity recorded.
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <ConfirmDADialog
      v-model="showConfirmDADialog"
      :orderId="parseInt(id)"
      :sourceLocation="addressDetails.source_location_id || null"
      :orderInfo="confirmDAOrderInfo"
      @daConfirmed="refreshPageData"
    />
    <CreateDADialog
      v-model="showCreateDADialog"
      :orderId="parseInt(id)"
      :sourceLocation="addressDetails.source_location_id"
      :orderInfo="createDAOrderInfo"
      :containerOverride="createDAContainerOverride"
      @daCreated="refreshPageData"
      @input="
        (val) => {
          if (!val) createDAContainerOverride = null;
        }
      "
    />
    <ChangeDestinationDialog
      v-model="showChangeLaneDialog"
      :orderId="parseInt(id)"
      :orderInfo="changeLaneOrderInfo"
      @destinationChanged="refreshPageData"
    />
  </div>
</template>

<script>
import ConfirmDADialog from "@/components/ModuleBased/dialogForms/ConfirmDADialog.vue";
import CreateDADialog from "@/components/ModuleBased/dialogForms/CreateDADialog.vue";
import ChangeDestinationDialog from "@/components/ModuleBased/dialogForms/ChangeDestinationDialog.vue";

export default {
  name: "EmergencyOrderDetails",
  components: {
    ConfirmDADialog,
    CreateDADialog,
    ChangeDestinationDialog,
  },
  data() {
    return {
      id: this.$route.params.id,
      loading: false,
      orderDetails: {},
      logs: [],
      logsLoading: false,
      showConfirmDADialog: false,
      showCreateDADialog: false,
      showChangeLaneDialog: false,
      changeLaneOrderInfo: {},
      createDAContainerOverride: null,
      expandedContainers: {},
    };
  },
  computed: {
    addressDetails() {
      return this.orderDetails.address_details || {};
    },
    timingDetails() {
      return this.orderDetails.timing_details || {};
    },
    executionDetails() {
      return this.orderDetails.execution_details || {};
    },
    daDetails() {
      return this.orderDetails.da_details || {};
    },
    subHireList() {
      const pk = this.orderDetails.sub_hire_pk;
      if (!pk) return [];
      if (Array.isArray(pk)) return pk;
      if (typeof pk === 'object') return [pk];
      return [{ id: pk, sub_hire_id: this.orderDetails.sub_hire_id }];
    },
    jobType() {
      const containerData = this.orderDetails.container_data || [];
      const jobTypes = [
        ...new Set(containerData.map((c) => c.job_type).filter(Boolean)),
      ];
      return jobTypes.join(", ") || "-";
    },
    containers() {
      const serverContainers = this.orderDetails.container_details;
      const rawNumbers = this.orderDetails.container_no;
      const mainType = this.orderDetails.container_type_name;

      if (Array.isArray(serverContainers) && serverContainers.length) {
        return serverContainers.map((c) => ({
          number: c.container_no || "-",
          type: c.container_type_name || mainType || "Standard",
          da_no: c.da_no || null,
          da_id: c.da_id || null,
          trip_no: c.trip_no || null,
          trip_id: c.trip_id || null,
          sub_hire_no: c.sub_hire_no || null,
          sub_hire_id: c.sub_hire_id || null,
          sub_hire_status: c.sub_hire_status || null,
          origin: c.origin || null,
          destination: c.destination || null,
          lane_id: c.lane_id || null,
        }));
      }

      if (Array.isArray(rawNumbers) && rawNumbers.length) {
        return rawNumbers.map((n) => ({
          number:
            typeof n === "object" ? n.container_no || n.container_number : n,
          type:
            typeof n === "object"
              ? n.container_type_name || mainType
              : mainType || "Standard",
          da_no: "-",
          origin: null,
          destination: null,
        }));
      }
      return [];
    },
    daLaneByDaNo() {
      const map = {};
      const list = this.daDetails.da_lane_list || [];
      for (const da of list) {
        map[da.da_no] = {
          lane_title: da.lane_title,
          origin: da.source_location,
          destination: da.destination_location,
        };
      }
      return map;
    },
    containerTableHeaders() {
      const hasDAs = (this.daDetails.da_lane_list || []).length > 0;
      const hasAnyLocationData =
        hasDAs || this.containers.some((c) => c.origin || c.destination);
      const base = [
        { text: "#", value: "index", sortable: false, width: "40" },
        { text: "Container Number", value: "number" },
        { text: "Type / Size", value: "type" },
        { text: "Delivery Advice", value: "da_no" },
      ];
      if (hasAnyLocationData) {
        base.push(
          { text: "Source", value: "origin" },
          { text: "Destination", value: "destination" },
        );
      }
      base.push({ text: "", value: "actions", sortable: false, width: "40" });
      return base;
    },
    containerMilestones() {
      return this.timingDetails.container_milestones || {};
    },
    hasContainersWithoutDA() {
      const details = this.orderDetails.container_details || [];
      return details.some((c) => !c.da_no);
    },
    confirmDAOrderInfo() {
      return {
        eToken: this.orderDetails.e_token || null,
        sourceLocation: this.addressDetails.source_location_name || null,
        customerName: this.orderDetails.customer_name || null,
        customerId: this.orderDetails.customer || null,
        laneId: this.orderDetails.lane || null,
        laneName: this.orderDetails.lane_title || null,
        vehicleNumber: this.executionDetails.vehicle_number || null,
        driverName: this.executionDetails.driver_name || null,
        containerData:
          (this.orderDetails.pending_container_data?.length
            ? this.orderDetails.pending_container_data
            : this.orderDetails.container_data) || [],
        daLaneList: this.daDetails.da_lane_list || [],
        isOog: this.orderDetails.is_oog || false,
        isHazardous: this.orderDetails.is_hazardous || false,
        isReefer: this.orderDetails.is_reefer || false,
        vendorId: this.orderDetails.vendor || null,
      };
    },
    createDAOrderInfo() {
      const pendingContainers = (
        this.orderDetails.container_details || []
      ).filter((c) => !c.da_no);
      const containerData = pendingContainers.length
        ? pendingContainers.map((c) => ({
            container_no: c.container_no,
            container_type: c.container_type_id,
            type_name: c.container_type_name,
          }))
        : this.orderDetails.container_data || [];
      return {
        eToken: this.orderDetails.e_token || null,
        sourceLocation: this.addressDetails.source_location_name || null,
        customerName: this.orderDetails.customer_name || null,
        customerId: this.orderDetails.customer || null,
        laneId: this.orderDetails.lane || null,
        laneName: this.orderDetails.lane_title || null,
        vehicleNumber: this.executionDetails.vehicle_number || null,
        driverName: this.executionDetails.driver_name || null,
        containerData,
        isSubHire: this.executionDetails.is_sub_hire || false,
        vendorId: this.orderDetails.vendor || null,
        vendorName: this.executionDetails.vendor_name || null,
        pickupArrival: this.timingDetails.pickup_arrival || null,
        pickupDeparture: this.timingDetails.pickup_departure || null,
        dropArrival: this.timingDetails.drop_arrival || null,
        dropDeparture: this.timingDetails.drop_departure || null,
      };
    },
  },
  methods: {
    subHireRouteName(status) {
      const map = {
        'Created': 'app-admin-sub-hire-created',
        'Pending': 'app-admin-sub-hire-pending',
      };
      return map[status] || 'app-admin-sub-hire-created';
    },
    getContainerMilestoneTs(containerNo, milestoneKey) {
      return (
        (this.containerMilestones[containerNo] || {})[milestoneKey] || null
      );
    },
    refreshPageData() {
      this.fetchData();
      this.fetchLogs();
    },
    fetchData() {
      this.loading = true;
      this.$api.emergencyOrder
        .getEmergencyOrderFullDetails(this.id)
        .then((res) => {
          this.orderDetails = res.data;
        })
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    fetchLogs() {
      this.logsLoading = true;
      this.$api.emergencyOrder
        .getLogs(this.id)
        .then((res) => {
          this.logs = res.data.results || res.data || [];
        })
        .catch(console.error)
        .finally(() => {
          this.logsLoading = false;
        });
    },
    copyVehicle(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          this.$toast && this.$toast.success("Copied!");
        })
        .catch(() => {});
    },
    openDA(daId) {
      this.$router
        .push({
          name: "app-admin-delivery-advice-details-id",
          params: { id: daId },
        })
        .catch(() => {});
    },
    openTrip(tripPk) {
      this.$router
        .push({ name: "app-admin-trip-details-id", params: { id: tripPk } })
        .catch(() => {});
    },
    openConfirmDA() {
      this.showConfirmDADialog = true;
    },
    openCreateDA() {
      this.showCreateDADialog = true;
    },
    openCreateDAForContainer(containerItem) {
      this.createDAContainerOverride = [
        {
          container_no: containerItem.number,
          container_type: null,
          type_name: containerItem.type,
          lane_id: containerItem.lane_id || null,
          origin: containerItem.origin || null,
          destination: containerItem.destination || null,
        },
      ];
      this.showCreateDADialog = true;
    },
    openChangeLane(containerItem) {
      const daLaneList = this.daDetails.da_lane_list || [];
      this.changeLaneOrderInfo = {
        eToken: this.orderDetails.e_token || null,
        customerName: this.orderDetails.customer_name || null,
        currentLane:
          containerItem.lane_title || this.orderDetails.lane_title || null,
        laneId: null,
        sourceLocationId: this.addressDetails.source_location_id || null,
        daList: this.daDetails.das || [],
        daLaneList,
        legIds: [],
        tripId: null,
        tripList: this.daDetails.trips || [],
        isSubHire: this.executionDetails.is_sub_hire || false,
        vendorName: this.executionDetails.vendor_name || null,
        containerData: [
          {
            container_no: containerItem.number,
            type_name: containerItem.type,
            container_type: containerItem.container_type_id || null,
          },
        ],
      };
      this.showChangeLaneDialog = true;
    },
    statusColor(status) {
      const map = {
        Created: "orange",
        Active: "primary",
        Completed: "success",
        Cancelled: "error",
      };
      return map[status] || "grey";
    },
    formatDate(dt) {
      if (!dt) return "-";
      const d = new Date(dt);
      if (isNaN(d)) return dt;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(
        d.getMonth() + 1,
      )}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
  },
  created() {
    this.refreshPageData();
  },
};
</script>

<style scoped>
.dashboard-bg {
  background-color: #f6f8fa;
}
.min-h-screen {
  min-height: 100vh;
}
.border {
  border: 1px solid #e1e4e8 !important;
}
.border-right {
  border-right: 1px solid #e1e4e8;
}
.border-bottom-dashed {
  border-bottom: 1px dashed #e1e4e8;
}
.uppercase-xs {
  text-transform: uppercase;
  font-size: 9px;
  letter-spacing: 0.5px;
}
.data-label {
  font-size: 10px;
  font-weight: 700;
  color: #6a737d;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.data-value {
  font-size: 14px;
  font-weight: 600;
  color: #24292e;
}
.tracking-tight {
  letter-spacing: -0.5px;
}

/* Tracking Milestones Proper Design */
.tracker-line-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 24px;
}
.tracker-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: all 0.3s ease;
}
.tracker-line {
  width: 2px;
  flex-grow: 1;
  z-index: 1;
  margin-top: -2px;
}
.tracking-item:last-child .tracker-line {
  display: none;
}
.active-milestone .tracker-dot {
  box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.05);
}
.pending-milestone .tracker-content {
  opacity: 0.6;
}

.milestone-col {
  background: #f6f8fa;
  transition: background 0.2s;
}
.milestone-col--active {
  background: #fff;
}

/* Activity Log vertical cards */
.log-scroll-area {
  max-height: 75vh;
  overflow-y: auto;
  background: white;
}
.log-scroll-area::-webkit-scrollbar {
  width: 4px;
}
.log-scroll-area::-webkit-scrollbar-thumb {
  background: #d1d5da;
  border-radius: 4px;
}
.log-v-card {
  border: 1px solid #e1e4e8;
  border-radius: 8px;
  overflow: hidden;
}
.log-v-card-status {
  padding: 6px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f6f8fa;
  border-bottom: 1px solid #e1e4e8;
}
.log-v-card-body {
  padding: 8px 12px;
}

/* Custom DataTable Styling */
.v-data-table >>> th {
  background-color: #f6f8fa !important;
  font-weight: 800 !important;
  font-size: 11px !important;
  text-transform: uppercase;
  color: #586069 !important;
  height: 44px !important;
}
.v-data-table >>> td {
  height: 48px !important;
  border-bottom: 1px solid #e1e4e8 !important;
}
</style>
